import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../../components/feature/Navigation";
import Footer from "../../components/feature/Footer";

interface ColoringItem {
  id: number;
  title: string;
  description: string;
  previewImage: string;
  downloadUrl: string;
  category: string;
}

const coloringItems: ColoringItem[] = [
  {
    id: 1,
    title: "Keanu Tails — Adventure Scene",
    description: "A fun coloring page inspired by the adventurous spirit of Keanu Tails. Great for kids who love action and exploration.",
    previewImage: "https://public.readdy.ai/ai/img_res/347980f15ebc20815137953026e809f9.jpg",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "Keanu Tails",
  },
  {
    id: 2,
    title: "My Neighbors are Vampires — Friendly Bat",
    description: "Color in the friendly neighborhood bat from our best-loved children's book. Perfect for Halloween or any time of year!",
    previewImage: "https://readdy.ai/api/search-image?query=adorable%20cute%20cartoon%20bat%20black%20and%20white%20coloring%20page%20line%20art%20simple%20outlines%20friendly%20vampire%20theme%20children%20printable%20coloring%20sheet%20minimal%20clean%20illustration&width=500&height=600&seq=coloring-2&orientation=portrait",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "My Neighbors Are Vampires",
  },
  {
    id: 3,
    title: "My Neighbors are Vampires — Spooky House",
    description: "Bring this spooky-cute neighborhood to life with your favorite colors. Bats, moons, and mystery included!",
    previewImage: "https://readdy.ai/api/search-image?query=cute%20cartoon%20haunted%20house%20black%20and%20white%20coloring%20page%20simple%20line%20art%20children%20coloring%20sheet%20minimal%20clean%20friendly%20spooky%20neighborhood%20moon%20bats%20printable%20illustration&width=500&height=600&seq=coloring-3&orientation=portrait",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "My Neighbors Are Vampires",
  },
  {
    id: 4,
    title: "Keanu Tails — Storybook Dog",
    description: "A lovable storybook dog ready for coloring. Kids can add their own colors and imagination to this friendly character.",
    previewImage: "https://readdy.ai/api/search-image?query=cute%20cartoon%20dog%20character%20black%20and%20white%20coloring%20page%20simple%20line%20art%20children%20printable%20coloring%20sheet%20minimal%20clean%20friendly%20happy%20puppy%20illustration%20storybook%20style&width=500&height=600&seq=coloring-4&orientation=portrait",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "Keanu Tails",
  },
  {
    id: 5,
    title: "Imagination Garden",
    description: "A whimsical garden scene filled with flowers, butterflies, and little surprises. Perfect for any young artist.",
    previewImage: "https://readdy.ai/api/search-image?query=whimsical%20garden%20coloring%20page%20black%20and%20white%20line%20art%20flowers%20butterflies%20mushrooms%20cute%20simple%20clean%20children%20printable%20coloring%20sheet%20minimal%20illustration%20magical%20nature%20scene&width=500&height=600&seq=coloring-5&orientation=portrait",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "Free Pages",
  },
  {
    id: 6,
    title: "Bookworm Reading Corner",
    description: "A cozy reading nook scene for book-loving kids. Color in the books, the lamp, and the comfy chair!",
    previewImage: "https://readdy.ai/api/search-image?query=cozy%20reading%20corner%20coloring%20page%20black%20and%20white%20line%20art%20bookshelf%20books%20lamp%20armchair%20cute%20simple%20children%20printable%20coloring%20sheet%20minimal%20clean%20illustration%20bookworm&width=500&height=600&seq=coloring-6&orientation=portrait",
    downloadUrl: "[DOWNLOAD_URL]",
    category: "Free Pages",
  },
];

const filterCategories = ["All", "Keanu Tails", "My Neighbors Are Vampires", "Free Pages"];

const features = [
  { icon: "ri-home-heart-line", title: "Home Use", desc: "Perfect for a rainy afternoon or a creative weekend activity for kids of all ages." },
  { icon: "ri-book-open-line", title: "Classroom Ready", desc: "Pair with the books for a complete reading and art activity. Teachers love these for group sessions." },
  { icon: "ri-gift-2-line", title: "Party & Events", desc: "Great for birthday parties, library events, and book club gatherings for young readers." },
];

export default function KidsCornerPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("animate-in"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".scroll-animate").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const filtered =
    activeCategory === "All" ? coloringItems : coloringItems.filter((c) => c.category === activeCategory);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: "#f0f8ff" }}>
      <style>{`
        .scroll-animate{opacity:0;transform:translateY(28px);transition:opacity .6s ease-out,transform .6s ease-out;}
        .scroll-animate.animate-in{opacity:1;transform:translateY(0);}
        .hover-lift{transition:transform .3s ease;}
        .hover-lift:hover{transform:translateY(-6px);}
      `}</style>

      <Navigation />

      {/* Hero — lighter and more playful */}
      <section
        className="pt-36 pb-16 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1a7a9e 0%, #2eb8d4 50%, #91E5F6 100%)" }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full opacity-15" style={{ backgroundColor: "#FFFFFF" }} />
          <div className="absolute bottom-0 left-20 w-40 h-40 rounded-full opacity-10" style={{ backgroundColor: "#FFFFFF" }} />
          <div className="absolute top-1/2 right-1/4 w-20 h-20 rounded-full opacity-20" style={{ backgroundColor: "#FFFFFF" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div
            className="inline-block px-5 py-2 text-xs font-bold tracking-widest uppercase rounded-full mb-6"
            style={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}
          >
            Free Printables
          </div>
          <h1
            className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-5"
            style={{ fontFamily: "'Fugaz One', cursive" }}
          >
            Kids Corner
          </h1>
          <p className="text-lg lg:text-xl text-white/90 max-w-2xl mx-auto mb-4 leading-relaxed">
            Free printable coloring pages for home, classrooms, and book-loving kids.
          </p>
          <p className="text-sm max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.75)" }}>
            A warm welcome to parents, teachers, and kids — all downloads are free, just click and print!
            New pages added regularly.
          </p>
        </div>
      </section>

      {/* Free badge banner */}
      <section
        className="py-4"
        style={{ background: "linear-gradient(90deg, #133C55 0%, #386FA4 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold tracking-wide" style={{ color: "#91E5F6" }}>
            All coloring pages are completely FREE — no sign-up required. Just download &amp; print!
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section className="py-6 bg-white border-b" style={{ borderColor: "#84D2F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {filterCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={
                  activeCategory === cat
                    ? { backgroundColor: "#133C55", color: "#FFFFFF", border: "1px solid #133C55" }
                    : { backgroundColor: "transparent", color: "#386FA4", border: "1px solid #84D2F6" }
                }
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Coloring pages grid */}
      <section className="py-16" style={{ background: "linear-gradient(180deg, #f0f8ff 0%, #d0eeff 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((item, index) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl overflow-hidden border scroll-animate hover-lift group"
                style={{ borderColor: "#84D2F6", transitionDelay: `${index * 80}ms` }}
              >
                {/* [COLORING_PREVIEW_IMAGE] */}
                <div className="relative w-full h-72 overflow-hidden bg-white">
                  <img
                    src={item.previewImage}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className="absolute top-3 left-3 px-3 py-1 text-xs font-bold tracking-widest uppercase rounded-full"
                    style={{ backgroundColor: "#91E5F6", color: "#133C55" }}
                  >
                    FREE
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full"
                      style={{ backgroundColor: "#d0eeff", color: "#386FA4" }}
                    >
                      {item.category}
                    </span>
                  </div>

                  {/* [COLORING_TITLE] */}
                  <h3
                    className="text-base font-bold mb-2 leading-snug"
                    style={{ fontFamily: "'Fugaz One', cursive", color: "#133C55" }}
                  >
                    {item.title}
                  </h3>

                  {/* [COLORING_DESCRIPTION] */}
                  <p className="text-xs leading-relaxed mb-5" style={{ color: "#386FA4" }}>
                    {item.description}
                  </p>

                  <div className="flex gap-2">
                    {/* [DOWNLOAD_URL] */}
                    <a
                      href={item.downloadUrl}
                      download
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-semibold rounded-lg transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-sm text-white"
                      style={{ backgroundColor: "#133C55" }}
                    >
                      <i className="ri-download-2-line" />
                      Download
                    </a>
                    {/* [PRINT_ACTION] */}
                    <button
                      onClick={handlePrint}
                      className="px-4 py-2.5 font-semibold rounded-lg border transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-sm"
                      style={{ borderColor: "#84D2F6", color: "#133C55" }}
                    >
                      <i className="ri-printer-line" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* More coming soon */}
          <div
            className="mt-12 p-10 rounded-2xl border text-center scroll-animate bg-white"
            style={{ borderColor: "#84D2F6" }}
          >
            <div
              className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5"
              style={{ backgroundColor: "#d0eeff" }}
            >
              <i className="ri-palette-line text-3xl" style={{ color: "#386FA4" }} />
            </div>
            <h3
              className="text-2xl font-bold mb-3"
              style={{ fontFamily: "'Fugaz One', cursive", color: "#133C55" }}
            >
              More Coloring Pages Coming Soon!
            </h3>
            <p className="text-sm max-w-sm mx-auto mb-6" style={{ color: "#386FA4" }}>
              Jen is creating new coloring pages for every book and season. Subscribe to be notified
              when new free printables are added.
            </p>
            <Link
              to="/writers-hub"
              className="inline-flex items-center gap-2 px-7 py-3 font-semibold rounded-lg transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-white text-sm"
              style={{ backgroundColor: "#133C55" }}
            >
              Visit Writer&apos;s Hub
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* For parents & teachers */}
      <section
        className="py-16"
        style={{ background: "linear-gradient(135deg, #133C55 0%, #1a7a9e 100%)" }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="scroll-animate">
            <div
              className="inline-block px-4 py-1 text-xs font-bold tracking-widest uppercase rounded-full mb-6"
              style={{ backgroundColor: "rgba(145,229,246,0.2)", color: "#91E5F6" }}
            >
              For Parents &amp; Educators
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-5"
              style={{ fontFamily: "'Fugaz One', cursive" }}
            >
              Designed for the Classroom &amp; Home
            </h2>
            <p className="text-base max-w-2xl mx-auto mb-10" style={{ color: "#d0eeff" }}>
              All coloring pages are free to download and print for personal, classroom, and educational
              use. Share the joy of reading and creativity with the children in your life!
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="p-6 rounded-2xl border text-left"
                  style={{ backgroundColor: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.12)" }}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-full mb-4"
                    style={{ backgroundColor: "rgba(145,229,246,0.2)" }}
                  >
                    <i className={`${f.icon} text-xl`} style={{ color: "#91E5F6" }} />
                  </div>
                  <h3
                    className="text-lg font-bold text-white mb-2"
                    style={{ fontFamily: "'Fugaz One', cursive" }}
                  >
                    {f.title}
                  </h3>
                  <p className="text-sm" style={{ color: "#d0eeff" }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section
        className="py-20"
        style={{ background: "linear-gradient(135deg, #d0eeff 0%, #91E5F6 100%)" }}
      >
        <div className="max-w-xl mx-auto px-6 text-center scroll-animate">
          <h2
            className="text-3xl lg:text-4xl font-bold mb-3"
            style={{ fontFamily: "'Fugaz One', cursive", color: "#133C55" }}
          >
            Get New Printables First
          </h2>
          <p className="text-sm mb-8" style={{ color: "#386FA4" }}>
            Be the first to know when new free coloring pages drop — sent occasionally, never spam.
          </p>
          <form
            id="newsletter-kids-corner"
            data-readdy-form
            action="https://readdy.ai/api/form/d6legiaqoe30lj0v7uhg"
            className="flex flex-col sm:flex-row gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
              try {
                await fetch("https://readdy.ai/api/form/d6legiaqoe30lj0v7uhg", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: data.toString() });
                form.reset();
              } catch { /* silent */ }
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="Your email address"
              className="flex-1 px-5 py-3.5 rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: "#FFFFFF", border: "1px solid #84D2F6", color: "#133C55" }}
              required
            />
            <button
              type="submit"
              className="px-7 py-3.5 font-semibold rounded-lg whitespace-nowrap cursor-pointer transition-all duration-300 hover:-translate-y-0.5 text-white"
              style={{ backgroundColor: "#133C55" }}
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
